import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { portalForgotSchema, portalLoginSchema, portalPasswordSchema } from '@app/shared';
import type {
  CardPaymentInput,
  InvoiceDto,
  PortalCompanyDto,
  PortalForgotInput,
  PortalHomeDto,
  PortalLoginInput,
  PortalOptionsDto,
  PortalPasswordInput,
  PortalQuoteDto,
} from '@app/shared';
import { APP_NAME } from '../../app/theme';
import { StatusChip } from '../../components/StatusChip';
import { TextInput, applyServerErrors } from '../../components/form';
import { useNotify } from '../../components/NotifyProvider';
import { ApiError, api, errorMessage } from '../../lib/api';
import { CardField } from '../billing/CardField';
import type { CardFieldHandle } from '../billing/CardField';

/**
 * The customer portal (switch `customer-portal`): a company's customers sign in here, at
 * /portal/<company>, and look after their own line. What it offers follows the reference's Customer
 * Portal settings (SC319 to SC327); the layout is ours, since its screens were not seen.
 */

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '-');

type Pay = { method: 'card' | 'wallet'; card?: CardPaymentInput };

function Frame({
  company,
  action,
  children,
}: {
  company?: PortalCompanyDto;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', flexDirection: 'column' }}>
      <Box
        component="header"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        {company?.logoUrl ? (
          <Box
            component="img"
            src={company.logoUrl}
            alt={company.name}
            sx={{ maxHeight: 44, maxWidth: 200 }}
          />
        ) : (
          <Typography variant="h2" component="p">
            {company?.name ?? ''}
          </Typography>
        )}
        {action}
      </Box>
      <Box component="main" sx={{ flex: 1, width: '100%', maxWidth: 720, mx: 'auto', p: 2 }}>
        {children}
      </Box>
      <Typography
        component="footer"
        variant="caption"
        color="text.secondary"
        align="center"
        sx={{ pb: 2 }}
      >
        Powered by {APP_NAME}
      </Typography>
    </Box>
  );
}

function SignIn({ slug, company }: { slug: string; company?: PortalCompanyDto }) {
  const queryClient = useQueryClient();
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState('');
  const [error, setError] = useState('');
  const login = useForm<PortalLoginInput>({
    resolver: zodResolver(portalLoginSchema),
    defaultValues: { mdn: '', password: '' },
  });
  const ask = useForm<PortalForgotInput>({
    resolver: zodResolver(portalForgotSchema),
    defaultValues: { mdn: '' },
  });
  const signIn = async (values: PortalLoginInput) => {
    setError('');
    try {
      await api.post(`/portal/${slug}/login`, values);
      await queryClient.invalidateQueries({ queryKey: ['portal', slug, 'me'] });
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const send = async (values: PortalForgotInput) => {
    setError('');
    try {
      const answer = await api.post<{ message: string }>(`/portal/${slug}/forgot`, values);
      setSent(answer.message);
      setForgot(false);
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  return (
    <Frame company={company}>
      <Paper variant="outlined" sx={{ maxWidth: 420, mx: 'auto', mt: 4, p: 3 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>
          {forgot ? 'Get a password' : 'Sign in'}
        </Typography>
        {sent ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {sent}
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {forgot ? (
          <Box
            component="form"
            noValidate
            onSubmit={(e) => void ask.handleSubmit(send)(e)}
            sx={{ display: 'grid', gap: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              First time here, or forgot your password? We will send a temporary password to the
              e-mail address or phone on your account.
            </Typography>
            <TextInput
              control={ask.control}
              name="mdn"
              label="Telephone Number"
              autoComplete="tel"
              required
            />
            <Button type="submit" variant="contained" disabled={ask.formState.isSubmitting}>
              Send me a password
            </Button>
            <Button onClick={() => setForgot(false)}>Back to sign in</Button>
          </Box>
        ) : (
          <Box
            component="form"
            noValidate
            onSubmit={(e) => void login.handleSubmit(signIn)(e)}
            sx={{ display: 'grid', gap: 2 }}
          >
            <TextInput
              control={login.control}
              name="mdn"
              label="Telephone Number"
              autoComplete="tel"
              required
            />
            <TextInput
              control={login.control}
              name="password"
              label="Password"
              type="password"
              autoComplete="current-password"
              required
            />
            <Button type="submit" variant="contained" disabled={login.formState.isSubmitting}>
              Sign in
            </Button>
            <Button onClick={() => setForgot(true)}>
              First time here or forgot your password?
            </Button>
          </Box>
        )}
      </Paper>
    </Frame>
  );
}

function ChangePassword({
  slug,
  forced,
  onDone,
}: {
  slug: string;
  forced: boolean;
  onDone: () => void;
}) {
  const notify = useNotify();
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<PortalPasswordInput>({
    resolver: zodResolver(portalPasswordSchema),
    defaultValues: { currentPassword: forced ? 'temporary' : '', newPassword: '' },
  });
  const save = async (values: PortalPasswordInput) => {
    try {
      await api.put(`/portal/${slug}/password`, values);
      notify.success('Your password is changed.');
      onDone();
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  };
  return (
    <Box
      component="form"
      noValidate
      onSubmit={(e) => void handleSubmit(save)(e)}
      sx={{ display: 'grid', gap: 2 }}
    >
      {forced ? (
        <Typography>Choose your own password to finish signing in.</Typography>
      ) : (
        <TextInput
          control={control}
          name="currentPassword"
          label="Current Password"
          type="password"
          autoComplete="current-password"
          required
        />
      )}
      <TextInput
        control={control}
        name="newPassword"
        label="New Password"
        type="password"
        autoComplete="new-password"
        helperText="At least 8 characters with a letter and a digit"
        required
      />
      <Button type="submit" variant="contained" disabled={isSubmitting}>
        Save password
      </Button>
    </Box>
  );
}

/** A way of paying with what it comes to: a saved or new card, or the wallet where it may pay. */
function PayDialog({
  slug,
  title,
  note,
  quoteOf,
  cents,
  walletCents,
  onPay,
  onClose,
  children,
}: {
  slug: string;
  title: string;
  note?: string;
  /** Asks the server what this comes to; `cents` is used when the amount is the customer's. */
  quoteOf?: { what: string; id?: string };
  cents?: number;
  /** The wallet can pay; absent where it cannot. */
  walletCents?: number;
  onPay: (pay: Pay) => Promise<void>;
  onClose: () => void;
  children?: ReactNode;
}) {
  const notify = useNotify();
  const card = useRef<CardFieldHandle>(null);
  const [method, setMethod] = useState<'card' | 'wallet'>('card');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const quote = useQuery({
    queryKey: ['portal', slug, 'quote', quoteOf, method],
    queryFn: () =>
      api.get<PortalQuoteDto>(`/portal/${slug}/quote`, {
        what: quoteOf!.what,
        id: quoteOf!.id,
        method,
      }),
    enabled: !!quoteOf,
  });
  const total = quoteOf ? quote.data?.totalCents : cents;
  const short = method === 'wallet' && total !== undefined && total > (walletCents ?? 0);
  const pay = async () => {
    setError('');
    setBusy(true);
    try {
      await onPay({
        method,
        ...(method === 'card' ? { card: await card.current?.collect() } : {}),
      });
      notify.success('Done. Thank you.');
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="portal-pay-title">
      <DialogTitle id="portal-pay-title">{title}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        {note ? <Typography variant="body2">{note}</Typography> : null}
        {children}
        {quote.error ? <Alert severity="error">{errorMessage(quote.error)}</Alert> : null}
        {total !== undefined ? (
          <Box>
            {quote.data?.taxCents ? (
              <Typography variant="body2" color="text.secondary">
                Includes {money(quote.data.taxCents)} tax
              </Typography>
            ) : null}
            <Typography variant="h2" component="p">
              Total {money(total)}
            </Typography>
          </Box>
        ) : null}
        {walletCents !== undefined ? (
          <RadioGroup
            aria-label="How to pay"
            value={method}
            onChange={(e) => setMethod(e.target.value as 'card' | 'wallet')}
          >
            <FormControlLabel value="card" control={<Radio />} label="Credit Card" />
            <FormControlLabel
              value="wallet"
              control={<Radio />}
              label={`Wallet (${money(walletCents)})`}
            />
          </RadioGroup>
        ) : null}
        {method === 'card' ? (
          <CardField
            ref={card}
            portal={{
              configPath: `/portal/${slug}/payment-config`,
              cardsPath: `/portal/${slug}/cards`,
            }}
          />
        ) : null}
        {short ? <Alert severity="warning">Your wallet does not hold enough.</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={busy || total === undefined || short}
          onClick={() => void pay()}
        >
          Pay {total !== undefined ? money(total) : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
    <Typography color="text.secondary">{label}</Typography>
    <Typography component="span" sx={{ fontWeight: 600, textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Paper variant="outlined" component="section" aria-label={title} sx={{ p: 2 }}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function Invoices({ rows }: { rows: InvoiceDto[] }) {
  if (!rows.length) return <Typography color="text.secondary">No invoices yet.</Typography>;
  return (
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {rows.map((i) => (
        <Box
          component="li"
          key={i.id}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600 }}>{i.invoiceType || i.description}</Typography>
            <Typography variant="caption" color="text.secondary">
              {i.invoiceNo} · {day(i.date)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontWeight: 600 }}>{money(i.balanceCents)}</Typography>
            <StatusChip status={i.status} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

type Action =
  | { kind: 'renew' }
  | { kind: 'change' }
  | { kind: 'topup' }
  | { kind: 'wallet' }
  | { kind: 'invoices' }
  | { kind: 'password' }
  | null;

function Home({
  slug,
  company,
  me,
}: {
  slug: string;
  company?: PortalCompanyDto;
  me: PortalHomeDto;
}) {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [action, setAction] = useState<Action>(null);
  const [planId, setPlanId] = useState('');
  const [topupKey, setTopupKey] = useState('');
  const [amount, setAmount] = useState('20.00');
  const close = () => {
    setAction(null);
    setPlanId('');
    setTopupKey('');
  };
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['portal', slug, 'me'] });
  const options = useQuery({
    queryKey: ['portal', slug, 'options'],
    queryFn: () => api.get<PortalOptionsDto>(`/portal/${slug}/options`),
    enabled: action?.kind === 'change' || action?.kind === 'topup',
  });
  const signOut = useMutation({
    mutationFn: () => api.post(`/portal/${slug}/logout`),
    onSuccess: () => queryClient.removeQueries({ queryKey: ['portal', slug, 'me'] }),
  });
  const autopay = useMutation({
    mutationFn: (enabled: boolean) => api.put(`/portal/${slug}/autopay`, { enabled }),
    onSuccess: () => void refresh(),
    onError: (err) => notify.error(err),
  });
  const post = async (path: string, body: object) => {
    await api.post(`/portal/${slug}/${path}`, body);
    await refresh();
  };
  const c = me.customer;
  const { offers } = me;
  const wallet = c.walletBalanceCents;
  const topups = [
    ...(options.data?.data ?? []).map((t) => ({ ...t, kind: 'data' as const })),
    ...(options.data?.minutes ?? []).map((t) => ({ ...t, kind: 'minutes' as const })),
  ];
  const topup = topups.find((t) => `${t.kind}:${t.id}` === topupKey);
  const plan = options.data?.plans.find((p) => p.id === planId);
  const amountCents = Math.round(Number(amount) * 100);
  return (
    <Frame company={company} action={<Button onClick={() => signOut.mutate()}>Sign out</Button>}>
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Typography variant="h1">Hello, {c.firstName}</Typography>
        {me.amountDueCents > 0 ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" onClick={() => setAction({ kind: 'invoices' })}>
                Pay
              </Button>
            }
          >
            You have {money(me.amountDueCents)} to pay.
          </Alert>
        ) : null}
        <Section title="My line">
          <Row label="Telephone Number" value={c.mdn || '-'} />
          <Row label="Plan" value={c.planName || '-'} />
          <Row label="Plan price" value={money(c.planPriceCents)} />
          <Row label="Plan expiry" value={day(c.planExpiresAt)} />
          <Row label="Status" value={<StatusChip status={c.status} />} />
          <Row label="Wallet" value={money(wallet)} />
          {me.renewBlocked ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {me.renewBlocked}
            </Alert>
          ) : null}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            {offers.renew && !me.renewBlocked ? (
              <Button variant="contained" onClick={() => setAction({ kind: 'renew' })}>
                Renew plan
              </Button>
            ) : null}
            {offers.changePlan ? (
              <Button variant="outlined" onClick={() => setAction({ kind: 'change' })}>
                Change plan
              </Button>
            ) : null}
            {offers.topup ? (
              <Button variant="outlined" onClick={() => setAction({ kind: 'topup' })}>
                Add a top-up
              </Button>
            ) : null}
            {offers.addWallet ? (
              <Button variant="outlined" onClick={() => setAction({ kind: 'wallet' })}>
                Add money
              </Button>
            ) : null}
          </Box>
        </Section>

        {offers.autopay && me.autopay ? (
          <Section title="Auto Pay">
            <FormControlLabel
              control={
                <Switch
                  checked={me.autopay.enabled}
                  disabled={autopay.isPending}
                  onChange={(e) => autopay.mutate(e.target.checked)}
                />
              }
              label="Renew my plan automatically when it runs out"
            />
          </Section>
        ) : null}

        {me.line ? (
          <Section title="Line details">
            <Row label="SIM" value={me.line.sim || '-'} />
            <Row label="Device" value={me.line.imei || '-'} />
            <Row label="Carrier" value={me.line.carrier || '-'} />
          </Section>
        ) : null}

        {me.unpaidInvoices?.length ? (
          <Section title="Unpaid invoices">
            <Invoices rows={me.unpaidInvoices} />
          </Section>
        ) : null}
        {me.invoices ? (
          <Section title="My invoices">
            <Invoices rows={me.invoices} />
          </Section>
        ) : null}

        <Section title="My account">
          <Row label="Account" value={c.accountNo} />
          <Row label="Name" value={`${c.firstName} ${c.lastName}`} />
          <Row label="Email Address" value={c.email || '-'} />
          <Row label="Address" value={c.address} />
          <Button sx={{ mt: 1 }} onClick={() => setAction({ kind: 'password' })}>
            Change password
          </Button>
        </Section>
      </Box>

      {action?.kind === 'renew' ? (
        <PayDialog
          slug={slug}
          title="Renew plan"
          note={c.planName}
          quoteOf={{ what: 'renew' }}
          walletCents={wallet}
          onPay={(pay) => post('renew', pay)}
          onClose={close}
        />
      ) : null}

      {(action?.kind === 'change' && !plan) || (action?.kind === 'topup' && !topup) ? (
        <Dialog open onClose={close} fullWidth maxWidth="xs">
          <DialogTitle>{action.kind === 'change' ? 'Change plan' : 'Add a top-up'}</DialogTitle>
          <DialogContent dividers>
            {options.isLoading ? <Typography>Loading…</Typography> : null}
            {options.error ? <Alert severity="error">{errorMessage(options.error)}</Alert> : null}
            {action.kind === 'change' && options.data ? (
              options.data.plans.length ? (
                <TextField
                  select
                  fullWidth
                  label="New plan"
                  value={planId}
                  onChange={(e) => setPlanId(e.target.value)}
                >
                  {options.data.plans.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} - {money(p.priceCents)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Typography>No other plan is offered for your line.</Typography>
              )
            ) : null}
            {action.kind === 'topup' && options.data ? (
              topups.length ? (
                <TextField
                  select
                  fullWidth
                  label="Top-up"
                  value={topupKey}
                  onChange={(e) => setTopupKey(e.target.value)}
                >
                  {topups.map((t) => (
                    <MenuItem key={`${t.kind}:${t.id}`} value={`${t.kind}:${t.id}`}>
                      {t.name} - {money(t.costCents)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Typography>No top-up is offered for your line.</Typography>
              )
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>Cancel</Button>
          </DialogActions>
        </Dialog>
      ) : null}
      {action?.kind === 'change' && plan ? (
        <PayDialog
          slug={slug}
          title="Change plan"
          note={plan.name}
          quoteOf={{ what: 'change', id: plan.id }}
          walletCents={wallet}
          onPay={(pay) => post('plan-change', { ...pay, planId: plan.id })}
          onClose={close}
        />
      ) : null}
      {action?.kind === 'topup' && topup ? (
        <PayDialog
          slug={slug}
          title="Add a top-up"
          note={topup.name}
          quoteOf={{ what: topup.kind, id: topup.id }}
          walletCents={wallet}
          onPay={(pay) => post('topups', { ...pay, kind: topup.kind, topupId: topup.id })}
          onClose={close}
        />
      ) : null}
      {action?.kind === 'wallet' ? (
        <PayDialog
          slug={slug}
          title="Add money"
          cents={amountCents}
          onPay={(pay) => post('wallet', { amountCents, card: pay.card })}
          onClose={close}
          note="Money in your wallet can pay for your plan and top-ups."
        >
          <TextField
            label="Amount ($)"
            value={amount}
            onChange={(e) =>
              /^\d{0,3}(\.\d{0,2})?$/.test(e.target.value) && setAmount(e.target.value)
            }
          />
        </PayDialog>
      ) : null}
      {action?.kind === 'invoices' ? (
        <PayDialog
          slug={slug}
          title="Pay what is due"
          cents={me.amountDueCents}
          walletCents={wallet}
          onPay={(pay) => post('pay-invoices', { ...pay, amountCents: me.amountDueCents })}
          onClose={close}
        />
      ) : null}
      {action?.kind === 'password' ? (
        <Dialog open onClose={close} fullWidth maxWidth="xs">
          <DialogTitle>Change password</DialogTitle>
          <DialogContent dividers>
            <ChangePassword slug={slug} forced={false} onDone={close} />
          </DialogContent>
        </Dialog>
      ) : null}
    </Frame>
  );
}

export function PortalPage() {
  const { slug = '' } = useParams();
  const queryClient = useQueryClient();
  const company = useQuery({
    queryKey: ['portal', slug, 'company'],
    queryFn: () => api.get<PortalCompanyDto>(`/portal/${slug}`),
    retry: false,
  });
  const me = useQuery({
    queryKey: ['portal', slug, 'me'],
    queryFn: () => api.get<PortalHomeDto>(`/portal/${slug}/me`),
    retry: false,
    enabled: !!company.data,
  });
  if (company.error)
    return (
      <Frame>
        <Alert severity="warning">This page is not available.</Alert>
      </Frame>
    );
  if (!company.data || me.isLoading)
    return (
      <Frame company={company.data}>
        <Typography>Loading…</Typography>
      </Frame>
    );
  if (me.error instanceof ApiError && me.error.status === 401)
    return <SignIn slug={slug} company={company.data} />;
  if (me.error)
    return (
      <Frame company={company.data}>
        <Alert severity="error">{errorMessage(me.error)}</Alert>
      </Frame>
    );
  if (!me.data) return <SignIn slug={slug} company={company.data} />;
  if (me.data.mustChangePassword)
    return (
      <Frame company={company.data}>
        <Paper variant="outlined" sx={{ maxWidth: 420, mx: 'auto', mt: 4, p: 3 }}>
          <Typography variant="h1" sx={{ mb: 2 }}>
            Choose your password
          </Typography>
          <ChangePassword
            slug={slug}
            forced
            onDone={() => void queryClient.invalidateQueries({ queryKey: ['portal', slug, 'me'] })}
          />
        </Paper>
      </Frame>
    );
  return <Home slug={slug} company={company.data} me={me.data} />;
}
